import React, {
  createContext,
  useState,
  useEffect,
  useContext,
} from "react";

import api from "../helpers/api.js";

import CustomModal from "@components/CustomModal";
import ImageImportTool from "@components/ImageImportTool/index.js";
import { Input } from "@fragments";

import axios from "axios";

import { useNavigate } from "react-router-dom";

import Config from "../config.js";

import { isPublicPath } from "../helpers/publicRoutes.js";

import "./styles/auth-context.css";

const AuthContext = createContext({ signed: false });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const navigate = useNavigate();

  window.navigate = navigate;

  useEffect(() => {
    if (loading) {
      handleInitiate();
    }
  }, [loading]);

  useEffect(() => {
    window.signed = !!user;
  }, [user]);

  async function handleInitiate() {
    const token = localStorage.getItem("token");

    var url = new URL(window.location.href);
    var response = url.searchParams.get("response");

    if (response) {
      let decoded = atob(response.replace(/ /g, "+"));
      try {
        decoded = decodeURIComponent(escape(decoded));
      } catch (e) { }

      const token = JSON.parse(decoded).token;

      localStorage.setItem("token", token);
      window.location.href = window.location.href.split("?")[0];
      return;
    }

    // Public routes (§10) must render for logged-out visitors and never bounce to
    // the auth server. We still try to resolve the user when a token is present
    // (the invite page gates accept/decline on account-email === invited_email),
    // but a missing/invalid session just renders the page in its logged-out state.
    const publicRoute = isPublicPath(window.location.pathname);

    if (token) {
      configApi();

      const user = await initializeUser();

      if (!user && !publicRoute) {
        handleGoToAuth();
      } else {
        setLoading(false);
      }
    } else if (publicRoute) {
      setLoading(false);
    } else {
      handleGoToAuth();
    }
  }

  function handleGoToAuth() {
    const currentUrl = window.location.href;
    const baseAuthUrl = Config.auth.split('?')[0];
    window.location.href = `${baseAuthUrl}?redirect_uri=${encodeURIComponent(currentUrl)}`;
  }

  async function initializeUser() {
    try {
      const { data } = await api.get("user");
      console.log("User loaded", data);

      if (!data?.id) return null;

      setUser({ ...data });
      localStorage.setItem("@user", JSON.stringify(data));
      setLoading(false);

      return data;
    } catch (e) {
      console.log(e);
    }
  }

  function configApi() {
    try {
      axios.defaults.baseURL = Config.apiEndpoint;

      const token = localStorage.getItem("token");

      // Merge — replacing the object would drop axios's default Accept header,
      // and without `Accept: application/json` Laravel answers failed
      // validation/auth with a 302 redirect instead of a 422/401 JSON.
      axios.defaults.headers.common = {
        ...axios.defaults.headers.common,
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      };
      axios.defaults.headers.withCredentials = true;
    } catch (e) {
      console.log(e);
    }
  }

  function applyAuthResult(authData) {
    if (!authData?.token) return false;

    const authUser = authData.user
      ? {
        ...authData.user,
        memberships: authData.memberships ?? authData.user.memberships,
      }
      : null;

    Config.apiToken = authData.token;
    axios.defaults.baseURL = Config.apiEndpoint;
    axios.defaults.headers.common = {
      ...axios.defaults.headers.common,
      Authorization: `Bearer ${authData.token}`,
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    };

    if (authUser) {
      localStorage.setItem("@user", JSON.stringify(authUser));
      setUser(authUser);
    }

    localStorage.setItem("token", authData.token);
    return true;
  }

  async function login(email, password) {
    try {
      const loginResult = await axios.post(Config.apiEndpoint + "/auth/login", {
        email,
        password,
      });

      return applyAuthResult(loginResult.data);
    } catch (e) {
      console.log(e);
    }
  }

  async function signOut() {
    if (loading || !window.signed) return;
    localStorage.clear();
    setTimeout(() => {
      window.navigate("/");
      window.location.reload();
    }, 200);
  }

  window.logout = signOut;

  async function handleCreateAccount(values) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const partShid = urlParams.get("part_shid");

      if (partShid) values.part_shid = partShid;

      const loginResult = await axios.post(
        Config.apiEndpoint + "/auth/signin",
        values
      );

      if (applyAuthResult(loginResult.data)) {
        window.location.href = "/";
        return true;
      }

      if (loginResult.data.success) {
        await login(values.email, values.password);
        window.location.href = "/";
        return true;
      } else {
        return false;
      }
    } catch (e) {
      console.log(e);
    }

    return false;
  }

  function handleOpenProfileConfigModal() {
    setProfileModalOpen(true);
  }
  function onUserUpdated(info) {
    localStorage.setItem("@user", JSON.stringify({ ...user, ...info }));
    setUser({ ...user, ...info });
    setProfileModalOpen(false);
    window.setSnackbar("Seu perfil foi atualizado com sucesso");
  }

  return (
    <AuthContext.Provider
      value={{
        signed: !!user,
        user,
        signOut,
        login,
        loading,
        handleCreateAccount,
        // Send the visitor to the auth server, returning them to the current URL
        // afterwards (redirect_uri). The public invite page (§6.9) uses this for
        // its "Entrar para responder" CTA.
        goToAuth: handleGoToAuth,

        handleOpenProfileConfigModal,
      }}
    >
      {children}

      <ProfileConfigModal
        key={Math.random()}
        onUpdated={onUserUpdated}
        open={profileModalOpen}
        onClose={() => {
          setProfileModalOpen(false);
        }}
      />
    </AuthContext.Provider>
  );
};

export default AuthContext;

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }

  return context;
}

async function dataUrlToFile(dataUrl, filename = "avatar.jpg") {
  const res = await fetch(dataUrl);
  const blob = await res.blob();

  return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

async function uploadDataUrlToAsset(dataUrl) {
  const file = await dataUrlToFile(dataUrl);
  const extension = file.name.split(".").pop() || "jpg";
  const contentType = file.type || "image/jpeg";
  const response = await api.post("content/upload-url", {
    filename: file.name,
    content_type: contentType,
    extension,
  });
  const { upload_url, final_url } = response.data || {};

  if (!upload_url || !final_url) {
    throw new Error("Could not prepare avatar upload");
  }

  const uploadResponse = await fetch(upload_url, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": contentType,
    },
    credentials: "omit",
    cache: "no-store",
  });

  if (!uploadResponse.ok) {
    throw new Error("Could not upload avatar image");
  }

  return final_url;
}

function ProfileConfigModal({ open, onClose, onUpdated }) {
  const { user } = useAuth();

  const [banner, setBanner] = useState(user?.avatar);
  const [username, setUsername] = useState(user?.username);

  const [loading, setLoading] = useState(false);

  async function handleSubmit() {
    if (username?.length < 8) {
      window.setSnackbar(
        "Insira um nome de usuário com no mínimo 8 caracteres"
      );
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.patch("profile", {
        username,
      });

      if (data?.success !== false) {
        onUpdated({
          ...(data && !data.success ? data : {}),
          username: data?.username ?? username,
        });
      }

      console.log(data);
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  }

  async function uploadAvatar(imageBase64) {
    setLoading(true);
    try {
      const avatarUrl = /^https?:\/\//.test(imageBase64)
        ? imageBase64
        : await uploadDataUrlToAsset(imageBase64);
      const { data } = await api.patch("user/avatar", {
        avatar: avatarUrl,
      });

      if (data?.success !== false) {
        onUpdated({
          ...(data && !data.success ? data : {}),
          avatar: data?.avatar ?? data?.path ?? avatarUrl,
        });
      }

      console.log(data);
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  }

  return (
    <>
      <CustomModal
        maxWidth={380}
        bottomButtonCancelText="Cancelar"
        bottomButtonSumbitText="Salvar perfil"
        handleSubmit={handleSubmit}
        hideSecondaryButton={false}
        title={"Configurar perfil"}
        handleClose={onClose}
        loading={loading}
        open={open}
        modalClass="config-profile-modal"
        width="100%"
        bodyWrapperStyle={{}}
        bodyComponent={
          <div className="profile-config-modal px-3">
            <h4 className=" bold mt-3 mb-1">Sua foto de perfil</h4>
            {!banner && <span className=" mb-1">Nenhuma foto selecionada</span>}
            <ImageImportTool
              src={banner}
              onCrop={(imageBase64) => {
                setBanner(imageBase64);
                uploadAvatar(imageBase64);
              }}
              size={120}
              rounded={true}
            />

            <Input.Text
              fullWidth
              helper="Insira seu nome de usuário"
              className="mt-3"
              placeholder="Seu nome de usuário"
              value={username}
              onInput={setUsername}
            />
          </div>
        }
      />
    </>
  );
}
